"use client";

import { ChevronDown, ChevronRight } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import {
  type BriefGroup,
  groupBriefsByCategory,
  MAX_VISIBLE_BRIEFS,
} from "../_lib/brief-grouping";
import type { ExploreBriefCard } from "../types";
import { SectionHeader } from "./section-header";
import {
  type BriefOpenMethod,
  type BriefSurface,
  useBriefRowTracking,
} from "./use-brief-analytics";
import {
  clearBriefDeepLinkParams,
  useBriefDeepLink,
} from "./use-brief-deep-link";

const LazyBriefDetailDialog = dynamic(
  async () => {
    const module = await import("./brief-detail-dialog");
    return module.BriefDetailDialog;
  },
  {
    ssr: false,
  }
);

// ─── Shared compact row ─────────────────────────────────────

function CompactBriefRow({
  brief,
  surface,
  positionInList,
  onClick,
}: {
  brief: ExploreBriefCard;
  surface: BriefSurface;
  positionInList: number;
  onClick: () => void;
}) {
  const { ref, handleClick } = useBriefRowTracking({
    brief,
    surface,
    positionInList,
    onClick,
  });
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-border-light)]"
      onClick={handleClick}
      ref={ref}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <p className="min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4] max-sm:line-clamp-2 sm:truncate">
        {brief.title}
      </p>
      {brief.tags.length > 0 && (
        <div className="hidden shrink-0 items-center gap-1 sm:flex">
          {brief.tags.map((tag) => (
            <span
              className="rounded-full bg-[var(--explore-tag-bg)] px-2 py-0.5 text-[0.65rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
      )}
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Grouped brief list ─────────────────────────────────────

function GroupedBriefList({
  groups,
  onBriefClick,
}: {
  groups: BriefGroup[];
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((slug: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((slug: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {groups.map((group) => {
        const isCollapsed = collapsedGroups.has(group.categorySlug);
        const isExpanded = expandedGroups.has(group.categorySlug);
        const hasMore = group.briefs.length > MAX_VISIBLE_BRIEFS;
        const remainingCount = group.briefs.length - MAX_VISIBLE_BRIEFS;

        return (
          <div key={group.categorySlug}>
            <button
              className="mb-1.5 flex w-full items-center gap-2 px-4 text-left lg:px-0"
              onClick={() => toggleGroup(group.categorySlug)}
              style={{ fontFamily: "var(--homepage-sans-stack)" }}
              type="button"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3.5 w-3.5 text-[var(--explore-text-muted)]" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5 text-[var(--explore-text-muted)]" />
              )}
              <span className="text-[0.78rem]">{group.categoryEmoji}</span>
              <span className="font-semibold text-[0.75rem] text-[var(--explore-text-primary)]">
                {group.categoryDisplayName}
              </span>
              <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
                ({group.briefs.length})
              </span>
            </button>

            {!isCollapsed && (
              <div>
                <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
                  <div className="divide-y divide-[var(--explore-border-light)]">
                    {group.briefs
                      .slice(0, MAX_VISIBLE_BRIEFS)
                      .map((brief, idx) => (
                        <CompactBriefRow
                          brief={brief}
                          key={brief.briefId}
                          onClick={() => onBriefClick(brief)}
                          positionInList={idx}
                          surface="explore_briefs_compact"
                        />
                      ))}
                  </div>
                  {hasMore && (
                    <div
                      className="grid"
                      style={{
                        gridTemplateRows: isExpanded ? "1fr" : "0fr",
                        transition:
                          "grid-template-rows 200ms cubic-bezier(0.23, 1, 0.32, 1)",
                      }}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="divide-y divide-[var(--explore-border-light)] border-[var(--explore-border-light)] border-t">
                          {group.briefs
                            .slice(MAX_VISIBLE_BRIEFS)
                            .map((brief, idx) => (
                              <CompactBriefRow
                                brief={brief}
                                key={brief.briefId}
                                onClick={() => onBriefClick(brief)}
                                positionInList={idx + MAX_VISIBLE_BRIEFS}
                                surface="explore_briefs_compact"
                              />
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {hasMore && (
                  <div className="-mt-3 mb-1 flex justify-center">
                    <button
                      className="rounded-full border border-[var(--explore-border-light)] bg-[var(--explore-bg)] px-3 py-1 text-[0.7rem] text-[var(--explore-text-muted)] shadow-sm transition-colors hover:text-[var(--explore-text-primary)]"
                      onClick={() => toggleExpand(group.categorySlug)}
                      style={{ fontFamily: "var(--homepage-sans-stack)" }}
                      type="button"
                    >
                      {isExpanded ? "Show less" : `Show ${remainingCount} more`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Export ──────────────────────────────────────────────────

function formatBriefsDate({ reportDate }: { reportDate: string }): string {
  const [year, month, day] = reportDate.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
  });
}

interface BriefsSectionProps {
  briefs: ExploreBriefCard[];
  briefsReportDate: string;
  exploreBase: string;
  className?: string;
  /** When on a category page, skip sub-grouping and show briefs flat under this category. */
  activeCategory?: {
    slug: string;
    displayName: string;
    emoji: string;
  };
}

export function BriefsSection({
  briefs,
  briefsReportDate,
  exploreBase,
  className,
  activeCategory,
}: BriefsSectionProps) {
  const [selectedBrief, setSelectedBrief] = useState<ExploreBriefCard | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openMethod, setOpenMethod] = useState<BriefOpenMethod>("click");

  const handleBriefClick = useCallback((brief: ExploreBriefCard) => {
    setOpenMethod("click");
    setSelectedBrief(brief);
    setDialogOpen(true);
  }, []);

  useBriefDeepLink({
    briefs,
    onOpen: useCallback((brief: ExploreBriefCard) => {
      setOpenMethod("deep_link");
      setSelectedBrief(brief);
      setDialogOpen(true);
    }, []),
  });

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      clearBriefDeepLinkParams();
    }
  }, []);

  const groups = useMemo(
    () => groupBriefsByCategory({ briefs, activeCategory }),
    [briefs, activeCategory]
  );
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const group = groups.find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [groups, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  if (briefs.length === 0) {
    return null;
  }

  return (
    <section className={className}>
      <SectionHeader
        subtitle={formatBriefsDate({ reportDate: briefsReportDate })}
        title="Briefs for"
      />

      <GroupedBriefList groups={groups} onBriefClick={handleBriefClick} />

      {selectedBrief ? (
        <LazyBriefDetailDialog
          brief={selectedBrief}
          categoryBriefs={categoryBriefs}
          exploreBase={exploreBase}
          onNavigateBrief={handleNavigateBrief}
          onOpenChange={handleDialogOpenChange}
          open={dialogOpen}
          openMethod={openMethod}
          surface="explore_briefs_compact"
        />
      ) : null}
    </section>
  );
}
